import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchProductPhoto } from "../../services/productMasterService";
import {
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogContent,
  Skeleton,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Box
} from "@mui/material";
import Cookies from "js-cookie";

function PublicPhotoProduct({ productId: propProductId }) {
  const params = useParams();
  const accessToken = Cookies.get("access_token") || "";
  const productId = propProductId || params?.productId;

  // Product & Photos States
  const [productDetail, setProductDetail] = useState({});
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const order = "desc";
  const [sortBy] = useState("created_at");
  const [loading, setLoading] = useState(false);

  // Product Group Master States
  const [productGroups, setProductGroups] = useState([]);
  const [activeProductGroup, setActiveProductGroup] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Project Lazy Loading States
  const [activeProject, setActiveProject] = useState(null);
  const [dataProject, setDataProject] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Asset Generation States
  const [generatingAsset, setGeneratingAsset] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ type: "", text: "" });

  // Zoom Modal
  const [open, setOpen] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);

  // --- FETCH PRODUCT GROUPS ---
  const fetchProductGroups = useCallback(async () => {
    if (!accessToken) return;
    setLoadingGroups(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/product-group-master`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) return;
      const json = await response.json();

      const formattedGroups = (json.data || []).map(group => ({
        id: group.id,
        label: group.name
      }));
      setProductGroups(formattedGroups);
    } catch (error) {
      console.error("Error fetching product groups:", error);
    } finally {
      setLoadingGroups(false);
    }
  }, [accessToken]);

  // Load product groups on mount
  useEffect(() => {
    fetchProductGroups();
  }, [fetchProductGroups]);

  // --- TRIGGER ASSET GENERATION ---
  const handleGenerateAsset = useCallback(async (projectId) => {
    if (!projectId || !accessToken) return;
    setGeneratingAsset(true);
    setAlertMessage({ type: "", text: "" });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/project-assets/generate/${projectId}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      const data = await response.json();

      if (response.status === 202) {
        setAlertMessage({ type: "info", text: data.message || "Asset generation started automatically." });
      } else {
        setAlertMessage({ type: "error", text: data.detail || "Failed to start generation automatically." });
      }
    } catch (error) {
      console.error("Error starting asset generation:", error);
      setAlertMessage({ type: "error", text: "Network error occurred during auto-generation." });
    } finally {
      setGeneratingAsset(false);
    }
  }, [accessToken]);

  // --- FETCH PROJECTS ---
  const fetchProject = useCallback(async (pageNumber = 1, search = "", isAppend = false, productGroupId = "") => {
    if (!accessToken) return;

    const targetGroupId = productGroupId || activeProductGroup?.id;
    if (!targetGroupId) {
      setDataProject([]);
      return;
    }

    setLoadingProjects(true);
    try {
      const pageSize = 15;
      let url = `${process.env.REACT_APP_API_URL}/api/v1/project?sort_by=created_at&sort_order=desc&page=${pageNumber}&size=${pageSize}`;

      url += `&column_name=product_group_master_id&column_operator=exact&column_value=${targetGroupId}`;

      if (search) {
        url += `&column_name=project_name&column_operator=LIKE&column_value=${encodeURIComponent(search)}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) return;

      const { data } = await response.json();

      const formattedData = data.map(item => ({
        label: item.project_name,
        id: item.survey_id, // Match criteria signature identifier reference
        project_type_id: item.project_type_id,
      }));

      setDataProject(prev => {
        const merged = isAppend ? [...prev, ...formattedData] : formattedData;

        // CRITICAL FIX: Ensure the currently active project remains in the options list
        // even if it's missing from the new paginated/filtered server results.
        if (activeProject && !merged.some(item => item.id === activeProject.id)) {
          return [activeProject, ...merged];
        }
        return merged;
      });

      setHasMoreProjects(data.length === pageSize);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoadingProjects(false);
    }
  }, [accessToken, activeProductGroup, activeProject]);

  // --- FETCH PHOTOS ---
  const fetchPhotoProduct = useCallback(async (pageNumber = 1, orderValue = order, surveyId = "") => {
    if (!productId) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/public-data/product/${productId}`);
      if (!response.ok) return;
      const data = await response.json();
      setProductDetail(data.data);

      const photoRes = await fetchProductPhoto(accessToken, surveyId, productId, pageNumber, limit, sortBy, orderValue);
      if (photoRes) {
        setPhotos(photoRes.items);
        setTotal(photoRes.total);
        setPage(photoRes.page);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [accessToken, productId, limit, sortBy, order]);

  // Initial Photo Load Trigger
  useEffect(() => {
    if (productId) {
      fetchPhotoProduct(1, order, "");
    }
  }, [productId, order, fetchPhotoProduct]);

  // --- MANUAL CHECK STATUS & REFRESH ---
  const handleCheckStatusAndRefresh = async () => {
    if (!accessToken) return;
    setCheckingStatus(true);
    setAlertMessage({ type: "", text: "" });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/project-assets/status`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.status === 404) {
        setAlertMessage({ type: "info", text: "No background tasks found. Refreshing photos..." });
      } else if (response.ok) {
        const data = await response.json();
        const currentStatus = data.status?.status;

        if (currentStatus === "progress") {
          setAlertMessage({ type: "warning", text: "Asset generation is still running in the background." });
        } else if (currentStatus === "success") {
          setAlertMessage({ type: "success", text: "Asset generation completed successfully!" });
        } else if (currentStatus === "failed") {
          setAlertMessage({ type: "error", text: "The last generation task failed." });
        }
      }

      await fetchPhotoProduct(1, order, activeProject?.id || "");

    } catch (error) {
      console.error("Error manual refresh execution:", error);
      setAlertMessage({ type: "error", text: "Failed to sync status updates." });
    } finally {
      setCheckingStatus(false);
    }
  };

  // --- DEBOUNCED SEARCH EFFECT ---
  useEffect(() => {
    let isMounted = true;

    const delayDebounceFn = setTimeout(() => {
      if (isMounted && activeProductGroup) {
        setProjectPage(1);
        fetchProject(1, searchQuery, false, activeProductGroup.id);
      }
    }, 400);

    return () => {
      isMounted = false;
      clearTimeout(delayDebounceFn);
    };
    // Exclude activeProject from dependencies to prevent typing fetch conflicts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeProductGroup]);

  // --- SCROLL EVENT HANDLER FOR DROPDOWN ---
  const handleDropdownScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
      !loadingProjects &&
      hasMoreProjects
    ) {
      const nextPage = projectPage + 1;
      setProjectPage(nextPage);
      fetchProject(nextPage, searchQuery, true, activeProductGroup?.id);
    }
  };

  const handleNext = () => {
    const next = page + 1;
    if (next <= Math.ceil(total / limit)) fetchPhotoProduct(next, order, activeProject?.id || "");
  };

  const handlePrev = () => {
    const prev = page - 1;
    if (prev >= 1) fetchPhotoProduct(prev, order, activeProject?.id || "");
  };

  const handleOpenImage = (img) => {
    setSelectedImg(img);
    setOpen(true);
  };

  if (!productId) {
    return <p style={{ padding: 5 }}>No product selected</p>;
  }

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "85vh", // Restricts component height inside Dialogs
        minWidth: 275,
        p: { xs: 2, sm: 4 },
        boxShadow: "none"
      }}
      component={Paper}
    >
      <Typography variant="h5" component="h4" sx={{ fontWeight: "bold" }}>
        PRODUCT PHOTOS
      </Typography>

      {/* SCROLLABLE CONTAINER FOR INFO, ALERTS, FILTERS, AND GALLERY */}
      <CardContent
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          px: { xs: 0, sm: 2 },
          py: 0,
          mt: 2,
          '&:last-child': { pb: 0 }
        }}
      >
        {/* INFO */}
        <Typography sx={{ fontSize: 14 }} color="text.secondary">
          {productDetail?.product_id}
        </Typography>
        <Typography variant="h5" sx={{ mb: 2 }}>{productDetail?.product_name}</Typography>

        {/* FEEDBACK SYSTEM ALERTS */}
        {alertMessage.text && (
          <Alert severity={alertMessage.type || "info"} sx={{ mb: 2, maxWidth: 650 }}>
            {alertMessage.text}
          </Alert>
        )}

        {/* CONTROLS ROW */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 3, width: "100%", maxWidth: 850 }}
        >
          {/* 1. PRODUCT GROUP SELECTOR */}
          <Autocomplete
            size="small"
            fullWidth
            options={productGroups}
            value={activeProductGroup}
            loading={loadingGroups}
            onChange={(event, newValue) => {
              setActiveProductGroup(newValue);
              setActiveProject(null);
              setSearchQuery("");
              setDataProject([]);
              setProjectPage(1);

              if (newValue) {
                fetchProject(1, "", false, newValue.id);
              } else {
                fetchPhotoProduct(1, order, "");
              }
            }}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Product Group"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingGroups ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* 2. PROJECT SELECTOR */}
          <Autocomplete
            size="small"
            fullWidth
            options={dataProject}
            value={activeProject}
            loading={loadingProjects}
            disabled={!activeProductGroup}
            filterOptions={(x) => x}
            getOptionLabel={(option) => option?.label || ""}
            inputValue={searchQuery}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === "input") setSearchQuery(newInputValue);
              if (reason === "clear") setSearchQuery("");
            }}
            onChange={(event, newValue) => {
              setActiveProject(newValue);
              setSearchQuery(newValue?.label || "");
              fetchPhotoProduct(1, order, newValue ? newValue.id : "");
              if (newValue?.id) handleGenerateAsset(newValue.id);
            }}
            isOptionEqualToValue={(option, value) => !value || option.id === value.id}
            ListboxProps={{
              onScroll: handleDropdownScroll,
              style: { maxHeight: "200px" }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={activeProductGroup ? "Select Project" : "Choose Product Group First"}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingProjects || generatingAsset ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleCheckStatusAndRefresh}
            disabled={checkingStatus || generatingAsset || loading || !activeProject}
            sx={{
              minWidth: { xs: "100%", sm: 130 },
              height: 40
            }}
            startIcon={checkingStatus ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {checkingStatus ? "Refreshing..." : "Refresh"}
          </Button>
        </Stack>

        {/* LOADING SKELETON */}
        {loading && (
          <ImageList cols={{ xs: 2, sm: 3 }} gap={10} sx={{ width: "100%" }}>
            {Array.from(new Array(6)).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={180} sx={{ borderRadius: 2 }} />
            ))}
          </ImageList>
        )}

        {/* EMPTY STATE */}
        {!loading && photos.length === 0 && <p>No photos found</p>}

        {/* GALLERY CONTAINER AREA */}
        {!loading && photos.length > 0 && (
          <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, mb: 1, width: "100%" }}>
            <ImageList
              variant="standard" // Changed from masonry to standard to guarantee horizontal grid alignment
              cols={4} // Fallback base column layout
              gap={16}
              sx={{
                width: "100%",
                my: 0,
                // Using explicit theme breakpoints to strictly force 3 to 4 images per row
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr) !important", // 2 images on tiny screens
                  sm: "repeat(3, 1fr) !important", // 3 images on tablet screens
                  md: "repeat(4, 1fr) !important", // 4 images on desktop screens
                }
              }}
            >
              {photos.map((item) => (
                <ImageListItem
                  onClick={() => handleOpenImage(item.file)}
                  key={item.id}
                  sx={{
                    overflow: "hidden",
                    borderRadius: "10px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    flexDirection: "column",
                    height: "100% !important" // Forces unified height constraints per row item
                  }}
                >
                  <Box sx={{ position: "relative", width: "100%", pt: "100%", overflow: "hidden" }}>
                    <img
                      src={item.file}
                      alt={item.id}
                      loading="lazy"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover", // Ensures different dynamic image sizes display uniformly
                        cursor: "zoom-in",
                        filter: "blur(6px)",
                        transition: "filter 0.3s ease",
                      }}
                      onLoad={(e) => {
                        e.currentTarget.style.filter = "blur(0px)";
                      }}
                    />
                  </Box>
                  <ImageListItemBar
                    position="below"
                    sx={{
                      padding: "8px 12px",
                      background: "#fff",
                      "& .MuiImageListItemBar-titleWrap": { p: 0 }
                    }}
                    title={
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          display: 'block',
                          whiteSpace: 'normal',
                          wordBreak: 'break-all'
                        }}
                      >
                        Kodeoutlet: {item.storeCode} <br />
                        Uploaded: {item.createdAt || "N/A"}
                      </Typography>
                    }
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}
      </CardContent>

      {/* PAGINATION PANEL */}
      {!loading && photos.length > 0 && (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{
            pt: 2,
            borderTop: "1px solid #e0e0e0",
            backgroundColor: "#fff"
          }}
        >
          <Button variant="contained" size="small" onClick={handlePrev} disabled={page === 1}>
            Prev
          </Button>
          <Typography variant="body2">
            Page {page} / {Math.ceil(total / limit)}
          </Typography>
          <Button variant="contained" size="small" onClick={handleNext} disabled={page >= Math.ceil(total / limit)}>
            Next
          </Button>
        </Stack>
      )}

      {/* IMAGE ZOOM */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 0, display: "flex", justifyContent: "center", bgcolor: "black" }}>
          {selectedImg && <img src={selectedImg} alt="zoom" style={{ maxWidth: "100%", maxHeight: "90vh", objectFit: "contain" }} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default PublicPhotoProduct;
